import { ActionConfirmation, type ActionOutcome } from "../ui/ActionConfirmation.js";
import {
  registrationSettledSummary,
  registrationSettlementWarning,
  type RegistrationSettledSummary,
} from "./registrationReconciliation.js";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { get, send, type Tutor } from "../api.js";
import { useAuth } from "../auth.js";
import { formatDate, isoDay, londonWallClockIso } from "../format.js";
import { useAsync } from "../useAsync.js";
import { idempotencyKey } from "./api.js";
import { AutomaticSettlementPolicyChoice } from "./AutomaticSettlementPolicyChoice.js";
import {
  automaticSettlementConfirmation,
  type AutomaticSettlementPolicy,
} from "./lessonBlockSaleModel.js";
import { formatMoney } from "./money.js";
import { asLocalInput, shiftBy } from "./reschedule.js";
import {
  CREDIT_CORRECTION_LABELS,
  automaticSettlementPolicyLabel,
  correctionAttempt,
  couponReviewItems,
  goodwillCreditInput,
  restorableUses,
  sourceLabel,
  transactionLabel,
  type CorrectionAttempt,
} from "./creditCorrectionModel.js";
import {
  couponLagsBlock,
  staffBooking,
  type LessonBlockDetail,
  type LessonBlockPaymentMethod,
  type LessonBlockProduct,
  type LessonBlockRefundPreview,
} from "./staff.js";
import { notifyStudentCreditStateChanged } from "./studentCreditEvents.js";
import { SellLessonBlock } from "./PaymentOperations.js";
import { TransferRemainingCredit } from "./TransferRemainingCredit.js";
import { useStudentCreditReads } from "./StudentCreditReads.js";
import { LegacyCreditRelease } from "./LegacyCreditRelease.js";

type OpenCorrection =
  | { kind: "legacy_migration" | "goodwill" | "sell" }
  | {
      kind:
        | "legacy_release"
        | "exceptional_adjustment"
        | "credit_restore"
        | "coupon_restore"
        | "expiry"
        | "refund"
        | "cancel";
      block: LessonBlockDetail;
    };

type CancelLessonBlockAttempt = {
  signature: string;
  key: string;
};

const localNow = () => asLocalInput(new Date().toISOString());
const laterExpiry = (current: string) => asLocalInput(shiftBy(current, 30, "days"));
const londonInputIso = (value: string) => {
  const [date = "", time = ""] = value.split("T");
  return londonWallClockIso(date, time);
};
/** The closure adjustment returned by the server, including revoked applied credits. */
function cancelledEntitlement(detail: LessonBlockDetail): string {
  const closure = detail.transactions.find((entry) => entry.transaction_type === "adjustment" && (
    entry.notes.startsWith("Unused credits removed when block was cancelled:") ||
    entry.notes.startsWith("All remaining block entitlement removed when block was cancelled:")
  ));
  return closure
    ? `${-closure.credit_delta} credits removed from this block’s entitlement.`
    : "The cancellation response did not report the removed credit count.";
}

const errorMessage = (cause: unknown, fallback: string) =>
  cause instanceof Error ? cause.message : fallback;

export async function cancelLessonBlock(
  lessonBlockId: string,
  reason: string,
  key: string,
): Promise<LessonBlockDetail> {
  // WMF-SIte's closure endpoint does not read Idempotency-Key, so a retry after
  // a lost response would otherwise 409 on an already-cancelled block. Mirror
  // ensureLessonBlockExpiry: check first, and on failure re-read and accept the
  // block if the cancellation actually landed.
  const path = `/staff-api/api/v1/lesson-blocks/${encodeURIComponent(lessonBlockId)}`;
  const isCancelled = (detail: LessonBlockDetail) => detail.block.status === "cancelled";

  const before = await get<LessonBlockDetail>(path);
  if (isCancelled(before)) return before;

  try {
    return await send<LessonBlockDetail>(
      "PATCH",
      `${path}/status`,
      { status: "cancelled", reason },
      { "Idempotency-Key": key },
    );
  } catch (cause: unknown) {
    const after = await get<LessonBlockDetail>(path);
    if (isCancelled(after)) return after;
    throw cause;
  }
}

export function CreditCorrections({
  studentId,
  studentName,
  refreshRevision = 0,
}: {
  studentId: string;
  studentName: string;
  refreshRevision?: number;
}) {
  const { me } = useAuth();
  const creditReads = useStudentCreditReads();
  const held = new Set(me?.capabilities ?? []);
  const manageAll = held.has("lesson_blocks.manage.all");
  const canManage = manageAll || held.has("lesson_blocks.manage.own_students");
  // Giving goodwill or carried-forward credit, correcting a balance and
  // selling a non-standard block are own-student management, like the rest of
  // this console. `create.own_students` is a legacy claim that grants nothing
  // and that no owner screen offers; requiring it hid these from every tutor.
  const canCreate = canManage;
  const [currentBlocks, setCurrentBlocks] = useState<LessonBlockDetail[]>([]);
  const [historyBlocks, setHistoryBlocks] = useState<LessonBlockDetail[]>([]);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [products, setProducts] = useState<LessonBlockProduct[]>([]);
  const [tutors, setTutors] = useState<Tutor[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [retryRevision, setRetryRevision] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [open, setOpen] = useState<OpenCorrection | null>(null);
  const [result, setResult] = useState<ActionOutcome | null>(null);
  const [registrationSummary, setRegistrationSummary] = useState<RegistrationSettledSummary | null>(null);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setProblem(null);
    Promise.all([
      creditReads.lessonBlocks(studentId, { view: "current", limit: 100 }),
      creditReads.lessonBlocks(studentId, {
        view: "history",
        limit: 10,
        offset: 0,
      }),
      staffBooking.lessonBlockProducts(),
      creditReads.tutors(),
    ])
      .then(([nextCurrent, history, nextProducts, tutorData]) => {
        if (!current) return;
        setCurrentBlocks(nextCurrent.blocks);
        setHistoryBlocks(history.blocks);
        setHistoryHasMore(history.offset + history.blocks.length < history.total);
        setProducts(nextProducts);
        setTutors(tutorData.tutors);
        setHasLoaded(true);
      })
      .catch((cause: unknown) => {
        if (current)
          setProblem(errorMessage(cause, "Could not load lesson credits."));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [studentId, refreshRevision, retryRevision, creditReads]);

  useEffect(() => {
    setRegistrationSummary(null);
    setResult(null);
    setOpen(null);
    setHasLoaded(false);
  }, [studentId]);

  async function refreshBlocks(appendHistory = false) {
    if (!appendHistory) creditReads.invalidate();
    const offset = appendHistory ? historyBlocks.length : 0;
    const [current, history] = await Promise.all([
      creditReads.lessonBlocks(studentId, { view: "current", limit: 100 }),
      creditReads.lessonBlocks(studentId, {
        view: "history",
        limit: 10,
        offset,
      }),
    ]);
    setCurrentBlocks(current.blocks);
    setHistoryBlocks((existing) => {
      if (!appendHistory) return history.blocks;
      const seen = new Set(existing.map((block) => block.block.id));
      return [
        ...existing,
        ...history.blocks.filter((block) => !seen.has(block.block.id)),
      ];
    });
    setHistoryHasMore(history.offset + history.blocks.length < history.total);
    return [...current.blocks, ...history.blocks];
  }

  const tutorNames = useMemo(
    () =>
      new Map(
        tutors.map((tutor) => [tutor.externalProviderId, tutor.displayName]),
      ),
    [tutors],
  );

  /**
   * Run one correction and report it.
   *
   * `unconfirmed` checks the answer for the thing the correction claims. A
   * response can succeed while its purpose did not — the backend answers 200
   * when a coupon's expiry could not be moved, and opens a review instead —
   * so a message it returns keeps the form open, with the correction's own
   * retry, rather than announcing something that did not happen.
   */
  async function apply(
    mutation: () => Promise<LessonBlockDetail>,
    description: string,
    unconfirmed?: (detail: LessonBlockDetail) => string | null,
  ): Promise<void> {
    const detail = await mutation();
    const warning = registrationSettlementWarning(detail);
    // This is evidence from the accepted mutation, independent of the later
    // authoritative balance read. An empty same-block replay must retain it.
    const settled = registrationSettledSummary(detail, registrationSummary);
    setRegistrationSummary(settled);
    let refreshedBlock: LessonBlockDetail | undefined;
    try {
      // The mutation response is not spliced into the balance/ledger list:
      // FastAPI is re-read before those authoritative snapshots change.
      refreshedBlock = (await refreshBlocks()).find((block) => block.block.id === detail.block.id);
    } catch (cause: unknown) {
      throw new Error(
        `${warning?.retryable ? `${warning.message} ` : ""}The correction was accepted, but the authoritative credit state could not be re-read. Retry this same correction before starting another. ${errorMessage(cause, "")}`.trim(),
      );
    }
    if (warning?.retryable) {
      // The sale form locks onto its accepted request and key for replay.
      throw new Error(warning.message);
    }
    const notDone = unconfirmed?.(detail) ?? null;
    if (notDone !== null) throw new Error(notDone);
    const titles: Record<string, string> = { legacy_release: "Legacy credits released", sell: "Lesson block added", goodwill: "Goodwill credits added", legacy_migration: "Carried-forward credits added", cancel: "Lesson block cancelled", credit_restore: "Lesson credit restored", coupon_restore: "Coupon use reconciled", expiry: "Lesson block expiry updated", refund: "External refund recorded", exceptional_adjustment: "Lesson credit balance adjusted" };
    setResult({ variant: warning ? "attention" : "success", title: titles[open?.kind ?? ""] ?? "Credit correction recorded", description: studentName,
      details: [{ value: `${description} Authoritative credit state refreshed.` }, ...(refreshedBlock && Number.isFinite(refreshedBlock.available_balance) ? [{ label: "Remaining block balance", value: `${refreshedBlock.available_balance} credits` }] : []), ...(open?.kind === "cancel" ? [{ value: cancelledEntitlement(detail) }] : []), ...(settled ? [{ value: <><p>{settled.heading}</p><ul>{settled.lessons.map((lesson, index) => <li key={index}>{lesson}</li>)}</ul>{settled.more > 0 && <p>and {settled.more} more</p>}</> }] : []), ...(warning ? [{ value: warning.message }] : [])] });
    setOpen(null);
  }

  function retryLoad() {
    creditReads.invalidate();
    setRetryRevision((revision) => revision + 1);
  }

  if (loading && !hasLoaded) {
    return (
      <section className="panel">
        <h2>Credits / lesson blocks</h2>
        <p className="empty">Loading authoritative credit state…</p>
      </section>
    );
  }

  if (problem && !hasLoaded) {
    return (
      <section className="panel">
        <h2>Credits / lesson blocks</h2>
        <div className="error">
          {problem ?? "Could not load lesson credits."}
        </div>
        <button type="button" onClick={retryLoad}>Retry</button>
      </section>
    );
  }

  const blocks = [...currentBlocks, ...historyBlocks];

  function renderBlock(block: LessonBlockDetail) {
    return (
      <CreditBlock
        key={block.block.id}
        detail={block}
        tutorName={
          tutorNames.get(block.block.provider_id) ??
          `Tutor ${block.block.provider_id}`
        }
        onOpen={(kind) => setOpen({ kind, block })}
        canCreate={canCreate}
        canManage={canManage}
        canReleaseLegacy={manageAll}
      >
        {open &&
        "block" in open &&
        open.block.block.id === block.block.id ? (
          <BlockCorrection
            open={{ ...open, block }}
            studentId={studentId}
            studentName={studentName}
            tutorName={
              tutorNames.get(block.block.provider_id) ??
              `Tutor ${block.block.provider_id}`
            }
            onCancel={() => setOpen(null)}
            onApply={apply}
          />
        ) : null}
      </CreditBlock>
    );
  }

  return (
    <section aria-label="Credits and lesson blocks">
      {loading && <p role="status" className="small muted">Updating authoritative credit state…</p>}
      {problem && <div role="alert" className="error">
        {problem} The last checked records are shown. Retry before making a correction.
        <button type="button" onClick={retryLoad}>Retry</button>
      </div>}
      <fieldset disabled={loading || problem !== null} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div className="spread credit-section-head">
        <div>
          <h2 style={{ marginBottom: 2 }}>Credits / lesson blocks</h2>
          <p className="small muted" style={{ marginTop: 0 }}>
            FastAPI&rsquo;s current ledger. Corrections append history; they do
            not edit a balance.
          </p>
        </div>
        {canCreate && (
          <div className="row credit-creation-actions">
            <button
              className="action-primary"
              type="button"
              onClick={() => setOpen({ kind: "sell" })}
            >
              Add / sell lesson block
            </button>
            <button
              className="action-tertiary"
              type="button"
              onClick={() => setOpen({ kind: "legacy_migration" })}
            >
              Record old-system credits
            </button>
            <button
              className="action-tertiary"
              type="button"
              onClick={() => setOpen({ kind: "goodwill" })}
            >
              Give goodwill credit
            </button>
          </div>
        )}
      </div>

      {result && <ActionConfirmation {...result} reveal />}
      {registrationSummary && !result && <div className="notice">
        <p><strong>{registrationSummary.heading}</strong></p>
        <ul>
          {registrationSummary.lessons.map((lesson, index) => <li key={index}>{lesson}</li>)}
        </ul>
        {registrationSummary.more > 0 && <p>and {registrationSummary.more} more</p>}
      </div>}

      {manageAll && (
        <TransferRemainingCredit
          studentId={studentId}
          studentName={studentName}
          blocks={blocks}
          products={products}
          onChanged={async (description) => {
            try {
              await refreshBlocks();
              setResult({ variant: "success", title: "Lesson credits transferred", description: studentName, details: [{ value: `${description} Authoritative credit state refreshed.` }] });
            } catch (cause: unknown) {
              throw new Error(
                `The transfer is already durable, but the authoritative credit state could not be re-read. Reconcile the existing operation rather than starting another transfer. ${errorMessage(cause, "")}`.trim(),
              );
            }
          }}
        />
      )}

      {open?.kind === "sell" ? (
        <SellLessonBlock
          key="sell"
          studentId={studentId}
          studentName={studentName}
          canSelectProvider={manageAll}
          ownProviderId={me?.booking.providerId ?? null}
          tutors={tutors}
          products={products}
          onCancel={() => setOpen(null)}
          onApply={apply}
        />
      ) : null}

      {open?.kind === "legacy_migration" || open?.kind === "goodwill" ? (
        <CreationCorrection
          key={open.kind}
          kind={open.kind}
          studentId={studentId}
          studentName={studentName}
          canSelectProvider={manageAll}
          ownProviderId={me?.booking.providerId ?? null}
          tutors={tutors}
          products={products}
          onCancel={() => setOpen(null)}
          onApply={apply}
        />
      ) : null}

      {blocks.length === 0 ? (
        <div className="panel empty">
          No lesson blocks are recorded for this student.
        </div>
      ) : (
        <>
          {currentBlocks.length > 0 && (
            <section aria-labelledby="current-credit-blocks-heading">
              <h3 id="current-credit-blocks-heading">Current / active</h3>
              {currentBlocks.map((block) => renderBlock(block))}
            </section>
          )}

          {historyBlocks.length > 0 && (
            <details className="ui-disclosure" style={{ marginTop: 14 }}>
              <summary>History</summary>
              {historyBlocks.map((block) => renderBlock(block))}
              {historyHasMore && (
                <div className="student-home-section-link">
                  <button
                    type="button"
                    className="quiet"
                    disabled={historyLoading}
                    onClick={() => {
                      setHistoryLoading(true);
                      setHistoryError(null);
                      void refreshBlocks(true)
                        .catch((cause: unknown) => {
                          setHistoryError(
                            errorMessage(cause, "Could not load older credit blocks."),
                          );
                        })
                        .finally(() => setHistoryLoading(false));
                    }}
                  >
                    {historyLoading ? "Loading…" : "Load older blocks"}
                  </button>
                  {historyError && <p className="error small">{historyError}</p>}
                </div>
              )}
            </details>
          )}
        </>
      )}
      </fieldset>
    </section>
  );
}

export function CreditBlock({
  detail,
  tutorName,
  onOpen,
  canCreate,
  canManage,
  canReleaseLegacy = false,
  children,
}: {
  detail: LessonBlockDetail;
  tutorName: string;
  onOpen: (
    kind: Exclude<OpenCorrection["kind"], "legacy_migration" | "goodwill" | "sell">,
  ) => void;
  canCreate: boolean;
  canManage: boolean;
  canReleaseLegacy?: boolean;
  children: ReactNode;
}) {
  const uses = restorableUses(detail);
  const reviews = couponReviewItems(detail);
  const active =
    detail.block.status === "active" ||
    detail.block.status === "requires_attention";
  const refundable = active && detail.balance > 0 && Number(detail.block.payment_amount) > 0;
  const paid = detail.block.source === "amelia" || detail.block.source === "staff";
  const migrationHeld = detail.block.status === "requires_attention" && detail.block.redemption_authority === "migration_hold" && detail.block.source === "amelia";
  const automaticSettlement = automaticSettlementPolicyLabel(
    detail.block.automatic_settlement_policy,
  );

  return (
    <article
      id={`credit-block-${detail.block.id}`}
      className="panel"
      aria-label={`${detail.product.display_name} lesson block`}
    >
      <div className="spread credit-block-heading">
        <div>
          <strong>
            {sourceLabel(detail.block.source)} · {detail.product.display_name}
          </strong>
          <div className="small muted">{tutorName}</div>
        </div>
        <span
          className={`tag${detail.block.status === "requires_attention" ? " warn" : ""}`}
        >
          {migrationHeld ? "Needs legacy review" : detail.block.status.replaceAll("_", " ")}
        </span>
      </div>

      <table style={{ marginTop: 8 }}>
        <tbody>
          <tr>
            <td className="small muted">Available now</td>
            <td>
              <strong>{detail.available_balance}</strong> credits
            </td>
          </tr>
          <tr>
            <td className="small muted">Ledger balance</td>
            <td>{detail.balance}</td>
          </tr>
          <tr>
            <td className="small muted">Original entitlement</td>
            <td>{detail.block.credits_purchased} credits</td>
          </tr>
          <tr>
            <td className="small muted">Expiry</td>
            <td>
              {formatDate(detail.block.expires_at)}
              {detail.is_expired ? " · expired" : ""}
            </td>
          </tr>
          <tr>
            <td className="small muted">Source</td>
            <td>{sourceLabel(detail.block.source)}</td>
          </tr>
          {automaticSettlement && (
            <tr>
              <td className="small muted">Automatic settlement</td>
              <td>{automaticSettlement}</td>
            </tr>
          )}
        </tbody>
      </table>

      {migrationHeld && <p className="notice warn small">These older paid credits are held until the original coupon and lessons already used are verified.{!canReleaseLegacy && " Ask an administrator to review this block."}</p>}
      {couponLagsBlock(detail) && (
        <p className="small notice warn" style={{ marginTop: 8 }}>
          Website coupon <code>{detail.coupon?.code}</code> still expires on{" "}
          {formatDate(detail.coupon?.expires_at ?? detail.block.expires_at)}, before this block.
          Open <strong>Extend expiry</strong> to move it.
        </p>
      )}

      <div className="row credit-block-actions">
        {migrationHeld && canReleaseLegacy && <button type="button" className="action-primary" onClick={() => onOpen("legacy_release")}>Review legacy credits</button>}
        {canCreate &&
          (detail.block.source === "legacy_migration" ||
            detail.block.source === "goodwill") && (
            <button
              className="action-tertiary"
              type="button"
              onClick={() => onOpen("exceptional_adjustment")}
            >
              {detail.block.source === "goodwill"
                ? "Adjust goodwill credit"
                : "Correct migration"}
            </button>
          )}
        {canCreate && canManage && paid && detail.balance > 0 && !migrationHeld && (
          <button
            className="action-tertiary"
            type="button"
            onClick={() => onOpen("exceptional_adjustment")}
          >
            Correct credit balance
          </button>
        )}
        {canManage && uses.length > 0 && !migrationHeld && (
          <button
            className="action-tertiary"
            type="button"
            onClick={() => onOpen("credit_restore")}
          >
            Restore consumed credit
          </button>
        )}
        {canManage && reviews.length > 0 && (
          <button
            className="action-tertiary"
            type="button"
            onClick={() => onOpen("coupon_restore")}
          >
            Resolve coupon review
          </button>
        )}
        {canManage && active && (
          <button
            className="action-tertiary"
            type="button"
            onClick={() => onOpen("expiry")}
          >
            Extend expiry
          </button>
        )}
        {canManage && refundable && (
          <button
            className="action-tertiary"
            type="button"
            onClick={() => onOpen("refund")}
          >
            Record external refund
          </button>
        )}
        {canManage && active && (
          <button
            className="action-danger"
            type="button"
            onClick={() => onOpen("cancel")}
          >
            Cancel lesson block
          </button>
        )}
      </div>

      {children}
      <CreditHistory detail={detail} />
    </article>
  );
}

function CreditHistory({ detail }: { detail: LessonBlockDetail }) {
  const expiryNotes = detail.block.notes
    .split("\n")
    .filter((line) => line.trim().startsWith("Expiry extended "));
  const hasHistory =
    detail.transactions.length > 0 ||
    detail.refunds.length > 0 ||
    (detail.coupon?.restorations.length ?? 0) > 0 ||
    expiryNotes.length > 0;

  return (
    <details style={{ marginTop: 14 }}>
      <summary className="small">Correction and credit history</summary>
      {!hasHistory && <p className="empty small">No history is available.</p>}
      {detail.transactions.map((transaction) => (
        <div className="small" style={{ marginTop: 10 }} key={transaction.id}>
          <strong>{transactionLabel(transaction, detail.block.source)}</strong>
          <div>
            {transaction.credit_delta > 0 ? "+" : ""}
            {transaction.credit_delta} credits ·{" "}
            {formatDate(transaction.created_at)} ·{" "}
            {transaction.created_by_staff_id
              ? "Staff member"
              : "Booking system"}{" "}
            · completed
          </div>
          {transaction.amelia_appointment_id !== null && (
            <div className="muted">
              Lesson appointment {transaction.amelia_appointment_id}
            </div>
          )}
          {transaction.notes.trim() && (
            <div className="muted">Reason: {transaction.notes}</div>
          )}
        </div>
      ))}
      {detail.coupon?.restorations.map((restoration) => (
        <div className="small" style={{ marginTop: 10 }} key={restoration.id}>
          <strong>{CREDIT_CORRECTION_LABELS.coupon_restore}</strong>
          <div>
            +{restoration.restored_uses} coupon use ·{" "}
            {formatDate(restoration.created_at)} ·{" "}
            {restoration.status.replaceAll("_", " ")}
          </div>
          <div className="muted">
            Lesson appointment {restoration.amelia_appointment_id} · booking{" "}
            {restoration.amelia_booking_id}
          </div>
          {restoration.last_error && (
            <div className="muted">Review reason: {restoration.last_error}</div>
          )}
        </div>
      ))}
      {detail.refunds.map((refund) => (
        <div className="small" style={{ marginTop: 10 }} key={refund.id}>
          <strong>{CREDIT_CORRECTION_LABELS.refund}</strong>
          <div>
            {refund.credits_refunded} credits ·{" "}
            {formatMoney(refund.amount_refunded, refund.payment_currency)} ·{" "}
            {formatDate(refund.refunded_at)} · Staff member · completed
          </div>
          {(refund.reason.trim() || refund.external_reference) && (
            <div className="muted">
              {refund.reason.trim() && `Reason: ${refund.reason.trim()}`}
              {refund.reason.trim() && refund.external_reference ? " · " : ""}
              {refund.external_reference && `reference ${refund.external_reference}`}
            </div>
          )}
        </div>
      ))}
      {expiryNotes.map((note, index) => (
        <div
          className="small"
          style={{ marginTop: 10 }}
          key={`${note}-${index}`}
        >
          <strong>{CREDIT_CORRECTION_LABELS.expiry}</strong>
          <div>
            {formatDate(detail.block.updated_at)} · Staff member · completed
          </div>
          <div className="muted">{note}</div>
        </div>
      ))}
    </details>
  );
}

export function CreationCorrection({
  kind,
  studentId,
  studentName,
  canSelectProvider,
  ownProviderId,
  tutors,
  products,
  onCancel,
  onApply,
}: {
  kind: "legacy_migration" | "goodwill";
  studentId: string;
  studentName: string;
  canSelectProvider: boolean;
  ownProviderId: number | null;
  tutors: Tutor[];
  products: LessonBlockProduct[];
  onCancel: () => void;
  onApply: (
    mutation: () => Promise<LessonBlockDetail>,
    description: string,
  ) => Promise<void>;
}) {
  const providers = tutors.filter(
    (tutor) => !tutor.left && tutor.externalProviderId !== null,
  );
  const [providerId, setProviderId] = useState(
    canSelectProvider ? (providers[0]?.externalProviderId ?? 0) : (ownProviderId ?? 0),
  );
  const {
    data: services,
    error: serviceError,
    loading: servicesLoading,
  } = useAsync(
    () =>
      providerId > 0
        ? staffBooking.providerServices(providerId)
        : Promise.resolve([]),
    [providerId],
  );
  const compatible = products.filter((product) =>
    (services ?? []).some(
      (service) => service.id === product.amelia_lesson_service_id,
    ),
  );
  const [productId, setProductId] = useState("");
  const product =
    compatible.find((item) => item.id === productId) ?? compatible[0];
  const [quantity, setQuantity] = useState(1);
  const [migrationDate, setMigrationDate] = useState(isoDay);
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");
  const [automaticSettlementPolicy, setAutomaticSettlementPolicy] =
    useState<AutomaticSettlementPolicy>("settle_outstanding_first");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<CorrectionAttempt | null>(null);
  const tutorName =
    providers.find((tutor) => tutor.externalProviderId === providerId)
      ?.displayName ?? `Tutor ${providerId}`;
  const label = CREDIT_CORRECTION_LABELS[kind];

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!product || providerId <= 0) return;
    const input =
      kind === "legacy_migration"
        ? {
            portal_student_id: studentId,
            provider_id: providerId,
            block_service_id: product.amelia_block_service_id,
            quantity,
            migration_date: migrationDate,
            reason,
            old_system_reference: reference.trim() || null,
          }
        : goodwillCreditInput({
            portal_student_id: studentId,
            provider_id: providerId,
            block_service_id: product.amelia_block_service_id,
            quantity,
            reason,
            automatic_settlement_policy: automaticSettlementPolicy,
          });
    const nextAttempt = correctionAttempt(attempt, kind, input, idempotencyKey);
    setAttempt(nextAttempt);
    setBusy(true);
    setProblem(null);
    try {
      await onApply(
        () =>
          kind === "legacy_migration"
            ? staffBooking.recordLegacyMigration(
                input as Parameters<
                  typeof staffBooking.recordLegacyMigration
                >[0],
                nextAttempt.key,
              )
            : staffBooking.grantGoodwillCredit(
                input as Parameters<typeof staffBooking.grantGoodwillCredit>[0],
                nextAttempt.key,
              ),
        kind === "legacy_migration"
          ? `${quantity} carried-forward ${product.display_name} credits recorded.`
          : `${quantity} goodwill ${product.display_name} credits recorded.`,
      );
    } catch (cause: unknown) {
      setProblem(
        errorMessage(cause, `Could not apply ${label.toLowerCase()}.`),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <CorrectionForm
      title={label}
      submitLabel={kind === "goodwill"
        ? `Add ${quantity} goodwill ${product?.display_name ?? "lesson"} credits for ${studentName}`
        : `Record ${quantity} carried-forward ${product?.display_name ?? "lesson"} credits for ${studentName}`}
      onSubmit={submit}
      onCancel={onCancel}
      problem={problem}
      busy={busy}
      submitDisabled={!product || providerId <= 0}
    >
      <Context
        studentName={studentName}
        tutorName={tutorName}
        serviceName={product?.display_name ?? "Choose a compatible service"}
        current="No entitlement is changed until this correction is submitted"
      />
      {canSelectProvider ? (
        <label>
          <span className="small muted">Tutor</span>
          <select
            value={providerId}
            onChange={(event) => {
              setProviderId(Number(event.target.value));
              setProductId("");
            }}
            required
          >
            {providers.map((tutor) => (
              <option key={tutor.id} value={tutor.externalProviderId ?? ""}>
                {tutor.displayName}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className="small">
          <span className="muted">Tutor: </span>
          {providerId > 0
            ? tutorName
            : "Your booking-provider account is not linked"}
        </p>
      )}
      <label>
        <span className="small muted">Compatible lesson service</span>
        <select
          value={product?.id ?? ""}
          onChange={(event) => setProductId(event.target.value)}
          disabled={servicesLoading || compatible.length === 0}
          required
        >
          {compatible.map((item) => (
            <option value={item.id} key={item.id}>
              {item.display_name}
            </option>
          ))}
        </select>
      </label>
      {serviceError && <div className="error small">{serviceError}</div>}
      {!servicesLoading && !serviceError && compatible.length === 0 && (
        <p className="small muted">
          No compatible lesson-block product is available for this tutor.
        </p>
      )}
      <label>
        <span className="small muted">Exact number of credits</span>
        <input
          type="number"
          min="1"
          value={quantity}
          onChange={(event) => setQuantity(Number(event.target.value))}
          required
        />
      </label>
      {kind === "legacy_migration" && (
        <label>
          <span className="small muted">Migration / as-of date</span>
          <input
            type="date"
            value={migrationDate}
            onChange={(event) => setMigrationDate(event.target.value)}
            required
          />
        </label>
      )}
      <Reason value={reason} onChange={setReason} />
      {kind === "legacy_migration" && (
        <label>
          <span className="small muted">Old-system reference (optional)</span>
          <input
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            maxLength={255}
          />
        </label>
      )}
      {kind === "goodwill" && (
        <AutomaticSettlementPolicyChoice
          name="goodwill-credit-allocation"
          value={automaticSettlementPolicy}
          onChange={(value) => {
            setAutomaticSettlementPolicy(value);
            setAttempt(null);
            setProblem(null);
          }}
          disabled={busy}
        />
      )}
      <div className="notice small">
        <strong>Proposed change:</strong>{" "}
        {kind === "legacy_migration"
          ? `Carry forward ${quantity} ${product?.display_name ?? "lesson"} credits for ${studentName} with ${tutorName}. No new payment will be recorded.`
          : `Give ${quantity} free ${product?.display_name ?? "lesson"} credits with ${tutorName} to ${studentName}${reason.trim() ? ` — reason: ${reason.trim()}. ` : ". "}${automaticSettlementConfirmation(automaticSettlementPolicy, quantity)}`}
      </div>
    </CorrectionForm>
  );
}

function BlockCorrection({
  open,
  studentId,
  studentName,
  tutorName,
  onCancel,
  onApply,
}: {
  open: Extract<OpenCorrection, { block: LessonBlockDetail }>;
  studentId: string;
  studentName: string;
  tutorName: string;
  onCancel: () => void;
  onApply: (
    mutation: () => Promise<LessonBlockDetail>,
    description: string,
  ) => Promise<void>;
}) {
  switch (open.kind) {
    case "legacy_release":
      return <LegacyCreditRelease detail={open.block} studentName={studentName} onCancel={onCancel} onApply={onApply} />;
    case "exceptional_adjustment":
      return (
        <ExceptionalAdjustment
          {...{
            detail: open.block,
            studentId,
            studentName,
            tutorName,
            onCancel,
            onApply,
          }}
        />
      );
    case "credit_restore":
      return (
        <RestoreCredit
          {...{
            detail: open.block,
            studentId,
            studentName,
            tutorName,
            onCancel,
            onApply,
          }}
        />
      );
    case "coupon_restore":
      return (
        <RestoreCoupon
          {...{ detail: open.block, studentName, tutorName, onCancel, onApply }}
        />
      );
    case "expiry":
      return (
        <ExtendExpiry
          {...{ detail: open.block, studentName, tutorName, onCancel, onApply }}
        />
      );
    case "refund":
      return (
        <RecordRefund
          {...{ detail: open.block, studentName, tutorName, onCancel, onApply }}
        />
      );
    case "cancel":
      return (
        <CancelBlock
          {...{ detail: open.block, studentName, tutorName, onCancel, onApply }}
        />
      );
  }
}

interface BlockFormProps {
  detail: LessonBlockDetail;
  studentName: string;
  tutorName: string;
  onCancel: () => void;
  onApply: (
    mutation: () => Promise<LessonBlockDetail>,
    description: string,
    unconfirmed?: (detail: LessonBlockDetail) => string | null,
  ) => Promise<void>;
}

function ExceptionalAdjustment({
  detail,
  studentId,
  studentName,
  tutorName,
  onCancel,
  onApply,
}: BlockFormProps & { studentId: string }) {
  const paid = detail.block.source === "amelia" || detail.block.source === "staff";
  const [delta, setDelta] = useState(paid ? -1 : 1);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<CorrectionAttempt | null>(null);
  const correctionKind = paid
    ? "paid_balance_adjustment"
    : detail.block.source === "goodwill"
      ? "goodwill_adjustment"
      : "legacy_adjustment";
  const original = detail.transactions.find(
    (transaction) => transaction.transaction_type === "grant",
  );
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (delta === 0) return;
    if (paid && (delta >= 0 || detail.balance + delta < 0)) {
      setProblem(
        `Enter a reduction between -1 and -${detail.balance}. Paid credits cannot be increased through a correction.`,
      );
      return;
    }
    const input = {
      credit_delta: delta,
      reason,
      original_transaction_id: original?.id ?? null,
    };
    const next = correctionAttempt(
      attempt,
      correctionKind,
      input,
      idempotencyKey,
    );
    setAttempt(next);
    setBusy(true);
    setProblem(null);
    try {
      await onApply(
        () =>
          staffBooking.adjustExceptionalCredit(detail.block.id, input, next.key),
        `${CREDIT_CORRECTION_LABELS[correctionKind]} ${signed(delta)} recorded.`,
      );
      notifyStudentCreditStateChanged(studentId);
    } catch (cause) {
      setProblem(
        errorMessage(
          cause,
          paid
            ? "Could not correct the paid credit balance."
            : "Could not record the exceptional credit adjustment.",
        ),
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <CorrectionForm
      title={CREDIT_CORRECTION_LABELS[correctionKind]}
      submitLabel={delta < 0
        ? `Remove ${-delta} ${detail.product.display_name} credits for ${studentName}`
        : `Add ${delta} ${detail.product.display_name} credits for ${studentName}`}
      submitDisabled={delta === 0}
      {...{ onCancel, problem, busy }}
      onSubmit={submit}
    >
      <Context
        {...{ studentName, tutorName }}
        serviceName={detail.product.display_name}
        current={
          paid
            ? `${detail.balance} ledger credits remaining from an original ${detail.block.credits_purchased}-credit paid block`
            : `${detail.available_balance} credits available from ${detail.block.credits_purchased} ${detail.block.source === "goodwill" ? "goodwill" : "carried forward"}`
        }
      />
      <label>
        <span className="small muted">
          {paid ? "Credits to remove" : "Signed correction"}
        </span>
        <input
          type="number"
          {...(paid
            ? { min: -Math.max(1, detail.balance), max: -1 }
            : {})}
          value={delta}
          onChange={(event) => setDelta(Number(event.target.value))}
          required
        />
      </label>
      <Reason value={reason} onChange={setReason} />
      <div className="notice small">
        <strong>Proposed change:</strong>{" "}
        {paid ? (
          <>
            Correct the ledger balance from <strong>{detail.balance}</strong> to{" "}
            <strong>{detail.balance + delta}</strong> credits. The original payment,
            original {detail.block.credits_purchased}-credit entitlement and credits
            already used for lessons stay in history. This does not issue a refund.
          </>
        ) : (
          <>
            Append {signed(delta)} credits to this{" "}
            {detail.block.source === "goodwill" ? "goodwill credit" : "migration"}.
            The original {detail.block.credits_purchased}-credit entry and consumed
            lessons stay in history.
          </>
        )}
      </div>
      {paid && delta < 0 && (
        <CouponStillLive
          detail={detail}
          creditsRemoved={-delta}
          noneLeft={detail.balance + delta <= 0}
        />
      )}
    </CorrectionForm>
  );
}

function CancelBlock({
  detail,
  studentName,
  tutorName,
  onCancel,
  onApply,
}: BlockFormProps) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<CancelLessonBlockAttempt | null>(null);
  const remaining = detail.balance;

  async function submit(event: FormEvent) {
    event.preventDefault();
    const normalizedReason = reason.trim();

    const signature = JSON.stringify({
      status: "cancelled",
      reason: normalizedReason,
    });
    const nextAttempt =
      attempt?.signature === signature
        ? attempt
        : { signature, key: idempotencyKey() };
    setAttempt(nextAttempt);
    setBusy(true);
    setProblem(null);
    try {
      await onApply(
        () => cancelLessonBlock(detail.block.id, normalizedReason, nextAttempt.key),
        `${detail.product.display_name} lesson block cancelled.`,
      );
    } catch (cause) {
      setProblem(errorMessage(cause, "Could not cancel this lesson block."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <CorrectionForm
      title="Cancel lesson block"
      submitLabel={remaining > 0 ? `Cancel this block (${remaining} credits)` : "Cancel this block (no unused credits)"}
      {...{ onCancel, problem, busy }}
      onSubmit={submit}
    >
      <Context
        {...{ studentName, tutorName }}
        serviceName={detail.product.display_name}
        current={`${detail.available_balance} credits available · ${detail.balance} ledger balance · ${sourceLabel(detail.block.source)}`}
      />
      <Reason value={reason} onChange={setReason} />
      <div className="notice small">
        <strong>Proposed change:</strong>{" "}
        {remaining > 0
          ? `Cancel this lesson block and remove its ${remaining} remaining ledger ${remaining === 1 ? "credit" : "credits"}. `
          : "Cancel this lesson block. No unused ledger credits remain to remove. "}
        Credits already used for lessons stay in history. Any payment previously
        recorded is left unchanged; this does not issue a refund.
      </div>
      <CouponStillLive detail={detail} creditsRemoved={remaining} noneLeft />
    </CorrectionForm>
  );
}

function RestoreCredit({
  detail,
  studentId,
  studentName,
  tutorName,
  onCancel,
  onApply,
}: BlockFormProps & { studentId: string }) {
  const candidates = restorableUses(detail);
  const [transactionId, setTransactionId] = useState(candidates[0]?.id ?? "");
  const selected =
    candidates.find((transaction) => transaction.id === transactionId) ??
    candidates[0];
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<CorrectionAttempt | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    const input = {
      amelia_appointment_id: selected.amelia_appointment_id as number,
      portal_student_id: studentId,
      notes: reason,
    };
    const next = correctionAttempt(
      attempt,
      "credit_restore",
      input,
      idempotencyKey,
    );
    setAttempt(next);
    setBusy(true);
    setProblem(null);
    try {
      await onApply(
        () => staffBooking.restoreCredit(detail.block.id, input, next.key),
        `Credit for lesson appointment ${input.amelia_appointment_id} restored.`,
      );
    } catch (cause) {
      setProblem(errorMessage(cause, "Could not restore that credit."));
    } finally {
      setBusy(false);
    }
  }
  return (
    <CorrectionForm
      title={CREDIT_CORRECTION_LABELS.credit_restore}
      submitLabel="Restore 1 credit"
      submitDisabled={!selected}
      {...{ onCancel, problem, busy }}
      onSubmit={submit}
    >
      <Context
        {...{ studentName, tutorName }}
        serviceName={detail.product.display_name}
        current={`${detail.available_balance} credits available`}
      />
      <label>
        <span className="small muted">Specific consumed lesson</span>
        <select
          value={selected?.id ?? ""}
          onChange={(event) => setTransactionId(event.target.value)}
          required
        >
          {candidates.map((transaction) => (
            <option key={transaction.id} value={transaction.id}>
              Appointment {transaction.amelia_appointment_id} ·{" "}
              {formatDate(transaction.created_at)}
            </option>
          ))}
        </select>
      </label>
      <Reason value={reason} onChange={setReason} />
      <div className="notice small">
        <strong>Proposed change:</strong> Restore exactly +1 credit for
        appointment {selected?.amelia_appointment_id ?? "—"}. The original debit
        remains in history; FastAPI will refuse it if it is not eligible.
        {liveCoupon(detail) && (
          <>
            {" "}The use also goes back on website coupon <code>{liveCoupon(detail)?.code}</code>{" "}
            in Amelia, so the family can book it.
          </>
        )}
      </div>
    </CorrectionForm>
  );
}

function RestoreCoupon({
  detail,
  studentName,
  tutorName,
  onCancel,
  onApply,
}: BlockFormProps) {
  const candidates = couponReviewItems(detail);
  const [itemId, setItemId] = useState(candidates[0]?.id ?? "");
  const selected =
    candidates.find((item) => item.id === itemId) ?? candidates[0];
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<CorrectionAttempt | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    const input = {
      appointment_id: selected.amelia_appointment_id,
      booking_id: selected.amelia_booking_id,
      reason,
    };
    const next = correctionAttempt(
      attempt,
      "coupon_restore",
      input,
      idempotencyKey,
    );
    setAttempt(next);
    setBusy(true);
    setProblem(null);
    try {
      await onApply(
        () => staffBooking.restoreCouponUse(detail.block.id, input, next.key),
        `Coupon use for appointment ${input.appointment_id} reconciled.`,
      );
    } catch (cause) {
      setProblem(errorMessage(cause, "Could not restore that coupon use."));
    } finally {
      setBusy(false);
    }
  }
  return (
    <CorrectionForm
      title={CREDIT_CORRECTION_LABELS.coupon_restore}
      submitLabel={`Restore ${selected?.restored_uses ?? 1} coupon use${(selected?.restored_uses ?? 1) === 1 ? "" : "s"}`}
      submitDisabled={!selected}
      {...{ onCancel, problem, busy }}
      onSubmit={submit}
    >
      <Context
        {...{ studentName, tutorName }}
        serviceName={detail.product.display_name}
        current={`Coupon ${detail.coupon?.status ?? "unavailable"} · ${detail.coupon?.current_limit ?? 0} uses remaining`}
      />
      <label>
        <span className="small muted">Review-required coupon use</span>
        <select
          value={selected?.id ?? ""}
          onChange={(event) => setItemId(event.target.value)}
          required
        >
          {candidates.map((item) => (
            <option key={item.id} value={item.id}>
              Appointment {item.amelia_appointment_id} · booking{" "}
              {item.amelia_booking_id}
            </option>
          ))}
        </select>
      </label>
      {selected?.last_error && (
        <p className="small muted">
          Why reconciliation stopped: {selected.last_error}
        </p>
      )}
      <Reason value={reason} onChange={setReason} />
      <div className="notice small">
        <strong>Proposed change:</strong> Restore exactly{" "}
        {selected?.restored_uses ?? 1} use on WMF&rsquo;s coupon for appointment{" "}
        {selected?.amelia_appointment_id ?? "—"}. This is not an arbitrary
        coupon-limit edit.
      </div>
    </CorrectionForm>
  );
}

function ExtendExpiry({
  detail,
  studentName,
  tutorName,
  onCancel,
  onApply,
}: BlockFormProps) {
  const [expiresAt, setExpiresAt] = useState(() =>
    laterExpiry(detail.block.expires_at),
  );
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<CorrectionAttempt | null>(null);
  /**
   * Retry only the coupon: the block's own expiry, sent exactly as stored.
   * The date field has minute precision, so re-entering it could not.
   */
  async function retryCoupon() {
    const input = {
      expires_at: detail.block.expires_at,
      reason: "",
    };
    const next = correctionAttempt(attempt, "expiry", input, idempotencyKey);
    setAttempt(next);
    setBusy(true);
    setProblem(null);
    try {
      await onApply(
        () => staffBooking.ensureLessonBlockExpiry(detail.block.id, input, next.key),
        `Website coupon moved to ${formatDate(input.expires_at)}.`,
        // Only once the answer shows it: the backend answers 200 when Amelia
        // did not take the new expiry, and lists the block for review.
        (saved) =>
          couponLagsBlock(saved)
            ? `The coupon's move could not be confirmed: website coupon ${saved.coupon?.code ?? ""} ` +
              `still expires on ${formatDate(saved.coupon?.expires_at ?? saved.block.expires_at)}. ` +
              "Try again, or leave it in the review list for the school to settle."
            : null,
      );
    } catch (cause) {
      setProblem(errorMessage(cause, "Could not move the coupon's expiry."));
    } finally {
      setBusy(false);
    }
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    let input: { expires_at: string; reason: string };
    try {
      input = { expires_at: londonInputIso(expiresAt), reason };
    } catch (cause: unknown) {
      setProblem(errorMessage(cause, "Enter an unambiguous London date and time."));
      return;
    }
    const next = correctionAttempt(attempt, "expiry", input, idempotencyKey);
    setAttempt(next);
    setBusy(true);
    setProblem(null);
    try {
      await onApply(
        () => staffBooking.ensureLessonBlockExpiry(detail.block.id, input, next.key),
        `Expiry extended to ${formatDate(input.expires_at)}.`,
      );
    } catch (cause) {
      setProblem(errorMessage(cause, "Could not extend the expiry."));
    } finally {
      setBusy(false);
    }
  }
  return (
    <CorrectionForm
      title={CREDIT_CORRECTION_LABELS.expiry}
      submitLabel={`Set expiry to ${expiresAt.replace("T", " ")}`}
      {...{ onCancel, problem, busy }}
      onSubmit={submit}
    >
      <Context
        {...{ studentName, tutorName }}
        serviceName={detail.product.display_name}
        current={`${detail.available_balance} credits · expires ${formatDate(detail.block.expires_at)} · ${sourceLabel(detail.block.source)}`}
      />
      <label>
        <span className="small muted">New expiry</span>
        <input
          type="datetime-local"
          value={expiresAt}
          min={asLocalInput(detail.block.expires_at)}
          onChange={(event) => setExpiresAt(event.target.value)}
          required
        />
      </label>
      <Reason value={reason} onChange={setReason} />
      <div className="notice small">
        <strong>Proposed change:</strong> Expiry{" "}
        {formatDate(detail.block.expires_at)} →{" "}
        {expiresAt ? expiresAt.replace("T", " ") : "—"}.
        Purchase/grant history is unchanged.
        {liveCoupon(detail) && (
          <>
            {" "}Website coupon <code>{liveCoupon(detail)?.code}</code> moves to the same expiry in
            Amelia.
          </>
        )}
      </div>
      {couponLagsBlock(detail) && (
        <div className="notice warn small credit-correction-coupon" role="note">
          Website coupon <code>{detail.coupon?.code}</code> still expires on{" "}
          {formatDate(detail.coupon?.expires_at ?? detail.block.expires_at)}, before this block. Bookings
          with it stop then.{" "}
          <button
            type="button"
            className="action-tertiary small"
            disabled={busy}
            onClick={() => void retryCoupon()}
          >
            Move the coupon to {formatDate(detail.block.expires_at)}
          </button>
        </div>
      )}
    </CorrectionForm>
  );
}

function RecordRefund({
  detail,
  studentName,
  tutorName,
  onCancel,
  onApply,
}: BlockFormProps) {
  const [credits, setCredits] = useState(Math.max(1, detail.balance));
  const [preview, setPreview] = useState<LessonBlockRefundPreview | null>(null);
  const [amount, setAmount] = useState("");
  const [amountEdited, setAmountEdited] = useState(false);
  const [method, setMethod] = useState<LessonBlockPaymentMethod>("other");
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [refundedAt, setRefundedAt] = useState(localNow);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<CorrectionAttempt | null>(null);
  const detailVersion = JSON.stringify(detail);
  const latestDetailVersion = useRef(detailVersion);
  latestDetailVersion.current = detailVersion;
  useEffect(() => {
    // An attempted request retains its exact preview and key for a safe replay.
    // Before submission, a changed block needs a fresh refund preview.
    if (attempt === null) setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailVersion]);
  async function loadPreview() {
    setBusy(true);
    setProblem(null);
    try {
      const next = await staffBooking.refundPreview(detail.block.id, credits);
      if (detailVersion !== latestDetailVersion.current) return;
      setPreview(next);
      if (!amountEdited) setAmount(next.suggested_amount);
    } catch (cause) {
      setProblem(errorMessage(cause, "Could not preview the refund."));
    } finally {
      setBusy(false);
    }
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!preview) return;
    let refundInstant: string;
    try {
      refundInstant = londonInputIso(refundedAt);
    } catch (cause: unknown) {
      setProblem(errorMessage(cause, "Enter an unambiguous London date and time."));
      return;
    }
    const input = {
      credits_refunded: preview.credits_to_refund,
      amount_refunded: amount,
      refund_method: method,
      external_reference: reference.trim() || null,
      reason,
      refunded_at: refundInstant,
      confirmed_externally: true as const,
    };
    const next = correctionAttempt(attempt, "refund", input, idempotencyKey);
    setAttempt(next);
    setBusy(true);
    setProblem(null);
    try {
      await onApply(
        () =>
          staffBooking.recordExternalRefund(detail.block.id, input, next.key),
        `${formatMoney(amount, preview.payment_currency)} external refund recorded.`,
      );
    } catch (cause) {
      setProblem(errorMessage(cause, "Could not record the external refund."));
    } finally {
      setBusy(false);
    }
  }
  if (!preview)
    return (
      <form
        className="panel"
        onSubmit={(event) => {
          event.preventDefault();
          void loadPreview();
        }}
        style={{ marginTop: 14 }}
      >
        <h3>{CREDIT_CORRECTION_LABELS.refund}</h3>
        {problem && <div className="error small">{problem}</div>}
        <Context
          {...{ studentName, tutorName }}
          serviceName={detail.product.display_name}
          current={`${detail.balance} unused ledger credits · ${formatMoney(detail.block.payment_amount, detail.block.payment_currency)} originally recorded`}
        />
        <label className="field-stack">
          <span className="small muted">Credits to refund</span>
          <input
            type="number"
            min="1"
            max={Math.max(1, detail.balance)}
            value={credits}
            onChange={(event) => {
              setCredits(Number(event.target.value));
              setPreview(null);
            }}
            required
          />
        </label>
        <div className="row">
          <button type="submit" disabled={busy}>
            {busy ? "Checking…" : "Preview refund"}
          </button>
          <button type="button" className="quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    );
  return (
    <CorrectionForm
      title={CREDIT_CORRECTION_LABELS.refund}
      submitLabel={`Record ${formatMoney(amount || preview.suggested_amount, preview.payment_currency)} refund · ${preview.credits_to_refund} credits removed`}
      {...{ onCancel, problem, busy }}
      onSubmit={submit}
    >
      <Context
        {...{ studentName, tutorName }}
        serviceName={detail.product.display_name}
        current={`${detail.balance} ledger credits remaining · Refund preview: ${preview.credits_remaining} refundable credits · ${formatMoney(preview.total_previously_refunded, preview.payment_currency)} previously refunded`}
      />
      <table>
        <tbody>
          <tr>
            <td className="small muted">Credits</td>
            <td>{preview.credits_to_refund}</td>
          </tr>
          <tr>
            <td className="small muted">FastAPI suggested amount</td>
            <td>{formatMoney(preview.suggested_amount, preview.payment_currency)}</td>
          </tr>
        </tbody>
      </table>
      <label>
        <span className="small muted">Amount already refunded externally</span>
        <input
          inputMode="decimal"
          value={amount}
          onChange={(event) => { setAmount(event.target.value); setAmountEdited(true); }}
          required
        />
      </label>
      <label>
        <span className="small muted">External method</span>
        <select
          value={method}
          onChange={(event) =>
            setMethod(event.target.value as LessonBlockPaymentMethod)
          }
        >
          <option value="stripe">Stripe</option>
          <option value="card_terminal">Card terminal</option>
          <option value="bank_transfer">Bank transfer</option>
          <option value="cash">Cash</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label>
        <span className="small muted">External reference (optional)</span>
        <input
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          maxLength={255}
        />
      </label>
      <label>
        <span className="small muted">When the external refund happened</span>
        <input
          type="datetime-local"
          value={refundedAt}
          onChange={(event) => setRefundedAt(event.target.value)}
          required
        />
      </label>
      <Reason value={reason} onChange={setReason} />
      <div className="notice small">
        <strong>Proposed change:</strong> Record that{" "}
        {formatMoney(amount || "0", preview.payment_currency)} was already refunded
        externally for {studentName}&rsquo;s {detail.product.display_name}{" "}
        block. This records the refund in WMF; it does not send money.
      </div>
      <CouponStillLive
        detail={detail}
        creditsRemoved={preview.credits_to_refund}
        noneLeft={preview.credits_to_refund >= preview.credits_remaining}
      />
    </CorrectionForm>
  );
}

function CorrectionForm({
  title,
  submitLabel,
  submitDisabled = false,
  onSubmit,
  onCancel,
  problem,
  busy,
  children,
}: {
  title: string;
  submitLabel: string;
  submitDisabled?: boolean;
  onSubmit: (event: FormEvent) => void;
  onCancel: () => void;
  problem: string | null;
  busy: boolean;
  children: ReactNode;
}) {
  return (
    <form
      className="panel credit-correction-form"
      style={{ marginTop: 14 }}
      onSubmit={onSubmit}
    >
      <h3>{title}</h3>
      {problem && <div className="error small">{problem}</div>}
      <div className="credit-correction-content">{children}</div>
      <div className="row" style={{ marginTop: 12 }}>
        <button type="submit" disabled={busy || submitDisabled}>
          {busy ? "Applying…" : submitLabel}
        </button>
        <button
          type="button"
          className="quiet"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * Until the backend changes the Amelia coupon itself, say so.
 *
 * A block bought on the website comes with an Amelia coupon, and the family
 * books through the public form with its code. Refunding, cancelling or
 * reducing the block changes WMF's ledger only: the coupon keeps working in
 * Amelia for the uses it had, and a lesson booked with it is recorded against
 * the block regardless (BOOK-AUDIT-003). So whoever makes the correction also
 * makes the matching change in Amelia, and confirms here that they will.
 *
 * A backend that reduces or hides the coupon in the same correction says so
 * with `follows_ledger`; then this only says what will happen.
 */
function CouponStillLive({
  detail,
  creditsRemoved,
  noneLeft,
}: {
  detail: LessonBlockDetail;
  creditsRemoved: number;
  noneLeft: boolean;
}) {
  const coupon = detail.coupon;
  if (!coupon || coupon.status === "retired" || creditsRemoved <= 0) return null;
  const uses = creditsRemoved === 1 ? "1 use" : `${creditsRemoved} uses`;
  if (coupon.follows_ledger) {
    // The backend makes the change itself. Asking staff to make it as well
    // would take the uses off twice.
    return (
      <div className="notice small credit-correction-coupon" role="note">
        <strong>Website coupon:</strong> this also{" "}
        {noneLeft ? (
          <>
            hides coupon <code>{coupon.code}</code> in Amelia, so it can no longer book lessons
          </>
        ) : (
          <>
            lowers coupon <code>{coupon.code}</code> in Amelia by {uses}
          </>
        )}
        . If Amelia&apos;s copy has changed in the meantime, nothing is guessed: it appears in
        your review list to finish in Amelia instead.
      </div>
    );
  }
  return (
    <div className="notice warn small credit-correction-coupon" role="note">
      <strong>The website coupon is not changed by this.</strong> Coupon{" "}
      <code>{coupon.code}</code> will still book lessons through the website
      form until you change it in Amelia. Straight after confirming, in Amelia
      go to Coupons, open <code>{coupon.code}</code> and{" "}
      {noneLeft ? (
        <>
          set it to <strong>Hidden</strong>, since no credits will be left on
          this block.
        </>
      ) : (
        <>
          lower its usage limit, and its limit per customer if one is set, by{" "}
          <strong>{uses}</strong>.
        </>
      )}
      <label className="row" style={{ marginTop: 8, alignItems: "start" }}>
        <input type="checkbox" required />
        <span>I will change coupon {coupon.code} in Amelia straight after this.</span>
      </label>
    </div>
  );
}

function Context({
  studentName,
  tutorName,
  serviceName,
  current,
}: {
  studentName: string;
  tutorName: string;
  serviceName: string;
  current: string;
}) {
  return (
    <dl className="credit-correction-context">
      <div>
        <dt className="small muted">Student</dt>
        <dd>
          <strong>{studentName}</strong>
        </dd>
      </div>
      <div>
        <dt className="small muted">Tutor / service</dt>
        <dd>
          {tutorName} · {serviceName}
        </dd>
      </div>
      <div>
        <dt className="small muted">Current authoritative state</dt>
        <dd>{current}</dd>
      </div>
    </dl>
  );
}

function Reason({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="credit-correction-reason">
      <span className="small muted">Note (optional)</span>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={2000}
      />
    </label>
  );
}

function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

/** The block's coupon, when the backend keeps it in step with the block. */
function liveCoupon(detail: LessonBlockDetail) {
  const coupon = detail.coupon;
  return coupon?.follows_ledger && coupon.status === "issued" ? coupon : null;
}
